import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SelectorCliente } from '../components/SelectorCliente';
import { DatosVisita, type DatosVisitaForm } from '../components/DatosVisita';
import { CabeceraPagina } from '../components/Armazon';
import {
  SelectorFranja,
  type Franja as FranjaSel,
} from '../components/SelectorFranja';
import { Aviso, Boton, Campo, Tarjeta } from '../components/ui';
import { ApiError, api } from '../lib/api';
import { franja } from '../lib/formato';
import { useAvisos } from '../lib/sesion';
import type { ClienteResumen, Solicitud } from '../lib/tipos';

/* ═══════════════════════════ Mejoras M-01 y M-02 ═══════════════════════════
   M-01 · El Comercial solo introduce lo imprescindible: el motivo de la
          petición y los productos que propone. La propuesta detallada y los
          comentarios siguen existiendo, pero plegados y opcionales, para
          quien quiera añadir contexto.
   M-02 · Las fechas se eligen sobre un calendario que muestra los bloqueos
          mientras se elige, y la fecha de fin se hereda de la de inicio.
   ═════════════════════════════════════════════════════════════════════════ */

export function NuevaSolicitud() {
  const navegar = useNavigate();
  const { avisar } = useAvisos();

  const [cliente, setCliente] = useState<ClienteResumen | null>(null);

  const [visita, setVisita] = useState<DatosVisitaForm>({ visitType: null, venueType: null, venueOther: '', productsToDiscuss: '' });
  const [motivo, setMotivo] = useState('');
  const [productos, setProductos] = useState('');
  const [propuesta, setPropuesta] = useState('');
  const [comentarios, setComentarios] = useState('');

  const [actual, setActual] = useState<FranjaSel | null>(null);
  const [alternativas, setAlternativas] = useState<FranjaSel[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [guardando, setGuardando] = useState<null | 'borrador' | 'envio'>(null);

  /** Lo que se enviará: la franja en edición más las alternativas guardadas. */
  const franjasFinales = useMemo(() => {
    const todas = [...alternativas];
    if (actual && new Date(actual.fin) > new Date(actual.inicio)) {
      const yaEsta = todas.some((f) => f.inicio === actual.inicio && f.fin === actual.fin);
      if (!yaEsta) todas.push(actual);
    }
    return todas;
  }, [actual, alternativas]);

  function guardarAlternativa() {
    if (!actual) return;
    setAlternativas((l) =>
      l.some((f) => f.inicio === actual.inicio && f.fin === actual.fin) ? l : [...l, actual],
    );
    setActual(null);
  }

  async function guardar(enviar: boolean) {
    setError(null);
    setErrores({});

    if (!cliente) {
      setError('Elija primero el cliente al que va dirigida la visita.');
      return;
    }
    if (motivo.trim().length < 5) {
      setErrores({ reason: 'Explique el motivo de la petición de la degustación.' });
      return;
    }
    if (enviar && (!visita.visitType || !visita.venueType)) {
      setError('Seleccione el tipo de visita y el lugar de celebración.');
      return;
    }
    if (visita.venueType === 'OTHER' && !visita.venueOther.trim()) {
      setError('Indique el lugar de celebración.');
      return;
    }
    if (enviar && visita.visitType === 'NO_PRODUCT' && !visita.productsToDiscuss.trim()) {
      setError('Indique los productos a comentar.');
      return;
    }
    if (enviar && visita.visitType !== 'NO_PRODUCT' && productos.trim().length < 2) {
      setErrores({ products: 'Indique qué productos propone mostrar.' });
      return;
    }
    if (enviar && franjasFinales.length === 0) {
      setError('Proponga al menos una fecha antes de enviarla al Jefe de Ventas.');
      return;
    }

    setGuardando(enviar ? 'envio' : 'borrador');
    try {
      const creada = await api.post<Solicitud>('/requests', { clientId: cliente.id });

      const actualizada = await api.patch<Solicitud>(`/requests/${creada.id}/commercial-block`, {
        version: creada.version,
        ...visita,
        reason: motivo.trim(),
        products: productos.trim(),
        tastingProposal: propuesta.trim() || undefined,
        commercialComments: comentarios.trim() || undefined,
      });

      for (const f of franjasFinales) {
        await api.post(`/requests/${creada.id}/slots`, {
          start: new Date(f.inicio).toISOString(),
          end: new Date(f.fin).toISOString(),
        });
      }

      if (enviar) {
        const ficha = await api.get<Solicitud>(`/requests/${creada.id}`);
        await api.post(`/requests/${creada.id}/send`, { version: ficha.version });
        avisar(`Solicitud ${creada.publicCode} enviada al Jefe de Ventas.`, 'hecho');
      } else {
        avisar(`Borrador ${actualizada.publicCode} guardado.`, 'hecho');
      }
      navegar(`/solicitudes/${creada.id}`, { replace: true });
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message);
        setErrores(e.fieldErrors);
      } else {
        setError('No se ha podido guardar la solicitud.');
      }
    } finally {
      setGuardando(null);
    }
  }

  return (
    <>
      <CabeceraPagina
        titulo="Nueva solicitud de visita"
        acciones={
          <Boton variante="secundario" onClick={() => navegar(-1)}>
            Cancelar
          </Boton>
        }
      />

      <div className="pila">
        {error && <Aviso tono="error">{error}</Aviso>}

        <Tarjeta titulo="1 · Cliente">
          {cliente ? (
            <div className="fila" style={{ justifyContent: 'space-between' }}>
              <div>
                <div className="codigo">Id. cliente {cliente.externalId}</div>
                <div style={{ fontWeight: 600, fontSize: 17 }}>{cliente.commercialName}</div>
                <div className="sutil">
                  {[cliente.city, cliente.province].filter(Boolean).join(' · ') || 'Sin localidad'}
                  {cliente.riskDescription ? ` · ${cliente.riskDescription}` : ''}
                </div>
              </div>
              <Boton variante="secundario" onClick={() => setCliente(null)}>
                Cambiar cliente
              </Boton>
            </div>
          ) : (
            <SelectorCliente onSeleccion={setCliente} />
          )}
        </Tarjeta>

        {/* M-01 · dos campos, y nada más. El resto queda plegado. */}
        <Tarjeta titulo="2 · Qué se pide">
          <div className="pila">
            <DatosVisita valor={visita} onCambio={(patch) => setVisita((v) => ({ ...v, ...patch }))}
              localidad={[cliente?.city, cliente?.province].filter(Boolean).join(' · ')} />
            <Campo
              etiqueta="Motivo de la petición de la degustación"
              error={errores.reason}
            >
              <textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                aria-invalid={errores.reason ? 'true' : undefined}
              />
            </Campo>

            {visita.visitType !== 'NO_PRODUCT' && (
            <Campo
              etiqueta="Productos que propone"
              error={errores.products}
            >
              <textarea
                value={productos}
                onChange={(e) => setProductos(e.target.value)}
                aria-invalid={errores.products ? 'true' : undefined}
              />
            </Campo>
            )}

            <details className="desplegable">
              <summary>Añadir detalle (opcional)</summary>
              <div className="pila">
                <Campo
                  etiqueta="Propuesta de degustación"
                  error={errores.tastingProposal}
                >
                  <textarea value={propuesta} onChange={(e) => setPropuesta(e.target.value)} />
                </Campo>
                <Campo
                  etiqueta="Comentarios para el Jefe de Ventas"
                  error={errores.commercialComments}
                >
                  <textarea
                    value={comentarios}
                    onChange={(e) => setComentarios(e.target.value)}
                    style={{ minHeight: 110 }}
                  />
                </Campo>
              </div>
            </details>
          </div>
        </Tarjeta>

        {/* M-02 · calendario con los bloqueos a la vista. */}
        <Tarjeta titulo="3 · Cuándo">
          <div className="pila">
            <SelectorFranja bloquearOcupadas
              valor={actual}
              onCambio={setActual}
            />

            <div className="fila" style={{ justifyContent: 'space-between' }}>
              <span className="sutil">
                {franjasFinales.length === 1
                  ? 'Se enviará una fecha.'
                  : `Se enviarán ${franjasFinales.length} fechas.`}
              </span>
              <Boton variante="secundario" tam="s" onClick={guardarAlternativa} disabled={!actual}>
                Guardar y proponer otra fecha
              </Boton>
            </div>

            {alternativas.length > 0 && (
              <div className="pila" style={{ gap: 'var(--r2)' }}>
                <span className="eyebrow">Fechas ya propuestas</span>
                {alternativas.map((f) => (
                  <div
                    key={`${f.inicio}-${f.fin}`}
                    className="fila"
                    style={{ justifyContent: 'space-between' }}
                  >
                    <span>{franja(f.inicio, f.fin)}</span>
                    <Boton
                      variante="plano"
                      tam="s"
                      onClick={() =>
                        setAlternativas((l) => l.filter((x) => x.inicio !== f.inicio || x.fin !== f.fin))
                      }
                    >
                      Quitar
                    </Boton>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Tarjeta>

        <div className="barra-acciones">
          <Boton
            variante="secundario"
            cargando={guardando === 'borrador'}
            disabled={guardando !== null || franjasFinales.length === 0}
            onClick={() => guardar(false)}
          >
            Guardar borrador
          </Boton>
          <Boton
            variante="clave"
            cargando={guardando === 'envio'}
            disabled={guardando !== null}
            onClick={() => guardar(true)}
          >
            Enviar al Jefe de Ventas
          </Boton>
        </div>
      </div>
    </>
  );
}
